import type { Scene } from '../types';
import { RadialChart } from '../components/RadialChart';

const SOURCES = [
  { bullet: '01', label: 'Multispectral satellite',  detail: 'visible, near-infrared, SWIR' },
  { bullet: '02', label: 'Synthetic aperture radar', detail: 'C-band backscatter, VV & VH' },
  { bullet: '03', label: 'Thermal infrared',          detail: 'land surface temperature' },
  { bullet: '04', label: 'Digital elevation model',   detail: 'slope, aspect, curvature' },
  { bullet: '05', label: 'Hydrological routing',      detail: 'predicted water flow paths' },
  { bullet: '06', label: 'Time-series phenology',     detail: 'seasonal NDVI rhythms, 2017–2024' },
];

export class SceneIntro implements Scene {
  readonly panel: HTMLElement;
  private radial!: RadialChart;
  private built = false;

  private static readonly DEMO_EMBED: number[] = Array.from(
    { length: 64 },
    (_, i) => Math.sin((i / 64) * Math.PI * 4) * 0.45,
  );

  constructor(root: HTMLElement) {
    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = 'intro';
    this.panel.innerHTML = `
      <div class="sm-intro-logo-bar">
        <img src="/gpf_sylva_logo.png" alt="GPF Sylva" class="sm-intro-logo" />
        <div>
          <div class="sm-panel-title" style="margin-bottom:2px;">Foundation Models</div>
          <div class="sm-panel-sub">AlphaEarth 2024 · 64-dim · 10 m resolution</div>
        </div>
      </div>

      <div class="sm-headline">
        Foundation models compress billions of satellite observations into a single
        habitat fingerprint per 10&nbsp;m pixel — enabling landscape-scale
        monitoring from a handful of field survey plots.
      </div>
      <div class="sm-intro-body">
        <p class="sm-intro-lead">
          Foundation models process billions of observations — weather records,
          satellite images in visual, radar, and infrared, terrain features
          like slope, elevation, and predictions of how water flows — and
          distil them into a single compact <em>habitat fingerprint</em>
          for every 10&nbsp;m pixel on Earth.
        </p>
        <p class="sm-intro-sources-label">Each 64-dimensional vector encodes:</p>
        <div class="sm-intro-sources" id="sm-intro-sources"></div>
        <p class="sm-intro-text">
          The scenes that follow show these embeddings for three contrasting
          environments across the Zambezi Escarpment — bare soil, open water,
          and riverside vegetation — before exploring how the signal separates
          the <strong style="color:var(--c-blue);">116 woody species</strong>
          recorded across Chewore&nbsp;North, Sapi, and&nbsp;Kapiri.
        </p>
        <p class="sm-intro-text" style="margin-top:10px;border-top:1px solid var(--glass-border);padding-top:10px;">
          This survey establishes an ecological baseline — the habitat
          fingerprint of each 10&nbsp;m cell before any intervention.
          Foundation models allow us to track anomalies, detect change, and
          measure the impact of conservation actions at scale, without deploying
          field teams to every plot.
        </p>
      </div>

      <div class="sm-intro-radial-wrap" id="sm-intro-radial-wrap"></div>`;
    root.appendChild(this.panel);
  }

  onEnter(): void {
    if (!this.built) {
      this.build();
      this.built = true;
    }
    this.radial.show(SceneIntro.DEMO_EMBED);
  }

  onLeave(): void {
    this.radial?.stop();
  }

  private build(): void {
    const sourcesEl = this.panel.querySelector<HTMLElement>('#sm-intro-sources')!;
    sourcesEl.innerHTML = SOURCES.map(s => `
      <div class="sm-intro-source-row">
        <span class="sm-intro-source-icon">${s.bullet}</span>
        <span class="sm-intro-source-label">${s.label}</span>
        <span class="sm-intro-source-detail">${s.detail}</span>
      </div>`).join('');

    const wrap = this.panel.querySelector<HTMLElement>('#sm-intro-radial-wrap')!;
    this.radial = new RadialChart(wrap, 200, 140);
  }
}
